import { useState } from "react";
import { toast } from "sonner";
import { FileDown, ImageDown, CheckCircle2, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { BRAND } from "../../lib/constants";
import { prepareReport } from "../../lib/laudo";
import { exportPdf, exportSummaryImage } from "../../lib/exporters";
import { SignaturePad } from "../common/SignaturePad";
import { StepTitle, Panel, ActionButton, Field, TextInput, ClassBadge } from "../common/Field";

export default function Step9Signature({ insp, update, save, photos }) {
  const [busy, setBusy] = useState("");
  const sig = insp.signature || {};
  const v = insp.vehicle || {};
  const set = (k) => (val) => update("signature", (prev) => ({ ...(prev || {}), [k]: val }));

  const run = async (kind) => {
    setBusy(kind);
    try {
      const { analysis, laudo, changed } = prepareReport(insp);
      const full = { ...insp, analysis, laudo, signature: { executor: v.executor, empresa: BRAND.empresa, ...sig } };
      if (changed) { update("analysis", analysis); update("laudo", laudo); }
      await save(true, changed ? { analysis, laudo } : {});
      if (kind === "pdf") await exportPdf(full, photos);
      else await exportSummaryImage(full, kind);
      toast.success(`Arquivo ${kind.toUpperCase()} exportado`);
    } catch (e) {
      toast.error(`Falha na exportação: ${e.message || e}`);
    } finally {
      setBusy("");
    }
  };

  const checks = [
    ["Veículo identificado", !!v.placa], ["Referência zero definida", !!insp.reference?.referencia_zero],
    ["Pontos medidos", (insp.points || []).length > 0], ["Análise gerada", !!insp.analysis], ["Laudo gerado", !!insp.laudo], ["Assinatura registrada", !!sig.image],
  ];

  return (
    <div className="flex flex-col gap-6">
      <StepTitle n={9} title="Assinatura e exportação" subtitle="Assine digitalmente e exporte o laudo em PDF (A4 retrato, alta qualidade) ou a ficha-resumo com o mapa de desalinhamento em PNG/JPG." />
      <div className="grid xl:grid-cols-[1fr_400px] gap-6 items-start">
        <Panel title="Assinatura do executor" testId="panel-signature">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Field label="Executor"><TextInput testId="sig-executor-input" value={sig.executor ?? v.executor} onChange={set("executor")} /></Field>
            <Field label="Registro profissional"><TextInput testId="sig-registro-input" value={sig.registro_profissional} onChange={set("registro_profissional")} /></Field>
            <Field label="Empresa"><TextInput testId="sig-empresa-input" value={BRAND.empresa} onChange={() => {}} disabled /></Field>
            <Field label="Data"><TextInput testId="sig-data-input" type="date" value={sig.data} onChange={set("data")} /></Field>
          </div>
          <SignaturePad value={sig.image} onChange={(img) => update("signature", (prev) => ({ ...(prev || {}), image: img, empresa: BRAND.empresa }))} />
        </Panel>
        <div className="flex flex-col gap-6">
          <Panel title="Exportação" testId="panel-export">
            <div className="mb-4"><div className="caption-label mb-2">Resultado</div><ClassBadge value={insp.analysis?.classificacao_final} testId="export-classificacao" /></div>
            <div className="flex flex-col gap-2">
              <ActionButton icon={FileDown} onClick={() => run("pdf")} disabled={!!busy} testId="btn-exportar-pdf" className="h-12 text-base">{busy === "pdf" ? "Gerando PDF..." : "Exportar PDF"}</ActionButton>
              <div className="grid grid-cols-2 gap-2">
                <ActionButton variant="outline" icon={ImageDown} onClick={() => run("png")} disabled={!!busy} testId="btn-exportar-png">{busy === "png" ? "..." : "PNG"}</ActionButton>
                <ActionButton variant="outline" icon={ImageDown} onClick={() => run("jpg")} disabled={!!busy} testId="btn-exportar-jpg">{busy === "jpg" ? "..." : "JPG"}</ActionButton>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Se a análise ou o laudo estiverem ausentes/desatualizados, serão gerados automaticamente antes da exportação.</p>
            </div>
          </Panel>
          <Panel title="Checklist do relatório" testId="panel-checklist">
            <ul className="flex flex-col gap-2">
              {checks.map(([l, ok]) => (
                <li key={l} className={cn("flex items-center gap-2 text-sm", ok ? "text-slate-200" : "text-slate-500")}>
                  {ok ? <CheckCircle2 size={16} className="text-emerald-400" /> : <Circle size={16} />}{l}
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}
